/**
 * Places: the objects that need inspection, clustered in zones, each with a state computed from real events
 * (docs/ACTIVITIES.md "Cycle 2 … Contract"). A PlaceObject is a named instance of a surface guide ("Front counter" is a
 * prep-counter; "Tables" ×6 is dining-tables). State is never set by hand: Done records an ActivityEvent with who and
 * when, and state follows from events, the surface's sourced interval, open reports and the supply shelf.
 *
 * Gates:
 * | O1 Derived | "clean" only when an event of the object's kind falls inside its interval; no event → not_started |
 * | O2 Sourced interval | Hours come only from the surface guide's howOften text that states a number ("every 4 hours");
 *   otherwise a suggested 24 h, flagged `suggested` with the reason shown |
 * | O3 Safety first | problem (open report on its zone or naming it) > out_of_stock (a supply it uses at 0) > timing |
 * | O4 One routine | routineFor mixes logs, tasks, due objects, open reports and low supplies in routine() order,
 *   each with How and GetIt-ready needs (supply keys from the sourced guides) |
 */
import type { ActivityItem, ActivityKind, ActivityNeed } from "./activities.ts";
import { kindOfTask, routine } from "./activities.ts";
import type { Supply } from "./actions.ts";
import type { Id, Incident, IsoDateTime, OrgKind, PermitType, TaskDefinition } from "./model.ts";
import type { Collection } from "./repo.ts";
import type { Correction } from "./corrections.ts";
import type { ExpectedCheck } from "./schedule.ts";
import { BUSINESS_TYPES, businessType } from "./opening.ts";
import { SURFACES, getSurface, supplyKeyFor, type SurfaceArea, type SurfaceGuide } from "./surfaces.ts";
import { seedForTask } from "./tasks.ts";
import { counts } from "./corrections.ts";

/* ─────────────── model ─────────────── */

export interface PlaceObject {
  id: Id;
  orgId: Id;
  locationId: Id;
  name: string;
  /** surfaces.ts id: the guide (steps, products, interval) this object follows. */
  surfaceId: string;
  /** Zone name (a section on the Places screen; also added to Location.areas so reports can name it). */
  zone: string;
  /** How many of it ("Tables" ×6). One Done covers all of them. */
  count?: number;
  notes?: string;
  /** Who services it (descaling, hood filters, grease trap): shown with maintenance on the Order screen (ordering.ts). */
  supplier?: { name: string; phone?: string; url?: string };
  createdAt?: IsoDateTime;
  createdBy?: Id;
}

/** Done on an object: who and when (server time). Clean and maintain are the only kinds an object records. */
export interface ActivityEvent {
  id: Id;
  orgId: Id;
  locationId: Id;
  objectId: Id;
  kind: Extract<ActivityKind, "clean" | "maintain">;
  at: IsoDateTime;
  by: Id;
  note?: string;
  /** "Not done after all" (corrections.ts): the Done no longer counts; the row stays in the history. */
  correction?: Correction;
}

/** Storage for places (migration 0008: place_objects, activity_events). Not exposed through the live-sync proxy. */
export interface PlaceRepo {
  objects: Collection<PlaceObject>;
  events: Collection<ActivityEvent>;
  /** One-tap quick logs (quicklog.ts; migration 0021 quick_logs). Missing on a database not yet migrated: the API keeps them in memory. */
  quick?: Collection<import("./quicklog.ts").QuickLog>;
}

export type ObjectStateName = "clean" | "due" | "overdue" | "problem" | "out_of_stock" | "not_started";
export interface ObjectState {
  state: ObjectStateName;
  lastAt?: IsoDateTime;
  /** Who did it last (the event's `by`, a user id; apps resolve the name). */
  lastBy?: string;
  dueAt?: IsoDateTime;
  reason: string;
  intervalHours?: number;
  suggested: boolean;
  /** The interval is learned from this team's own Done history (quicklog.ts learnedIntervals): "Learned from your team's logs". */
  learned?: true;
}

/* ─────────────── zones ─────────────── */

export type ZonePreset = "cafe" | "restaurant" | "truck" | "church_hall";

interface PresetDef {
  zones: string[];
  /** Surface area → zone. Areas not listed are left out of the starter set. */
  area: Partial<Record<SurfaceArea, string>>;
  /** Per-surface zone overrides; also brings in a surface from an unlisted area. */
  put?: Record<string, string>;
  /** Surfaces left out of this preset's starter set (specialty or event-only gear). */
  skip?: string[];
}

const PRESETS: Record<ZonePreset, PresetDef> = {
  cafe: {
    zones: ["Bar", "Front counter", "Seating", "Restroom", "Storage", "Outside"],
    area: { coffee_bar: "Bar", bar_beverage: "Bar", prep: "Front counter", dish: "Front counter", cold_storage: "Storage", dry_storage: "Storage", front_of_house: "Seating", restroom: "Restroom", floors_walls: "Seating", trash_outside: "Outside" },
    put: { oven: "Front counter", microwave: "Front counter", "payment-screen": "Front counter", "pastry-display-case": "Front counter", "kitchen-floor": "Front counter", "trash-cans-indoor": "Front counter", "mop-sink": "Storage" },
    skip: ["slicer", "walk-in", "grease-trap", "grill-grates", "coolers-ice-chests", "hardwood-floor", "trash-cans-outdoor"],
  },
  restaurant: {
    zones: ["Front of house", "Line", "Prep", "Dish", "Walk-in and storage", "Restroom", "Outside"],
    area: { bar_beverage: "Front of house", cooking_line: "Line", prep: "Prep", dish: "Dish", cold_storage: "Walk-in and storage", dry_storage: "Walk-in and storage", front_of_house: "Front of house", restroom: "Restroom", floors_walls: "Front of house", trash_outside: "Outside" },
    put: { "drip-brewer": "Front of house", "kitchen-floor": "Line", "floor-drains": "Line", "walls-ceilings": "Line", "mop-sink": "Dish", "trash-cans-indoor": "Dish" },
    skip: ["grill-grates", "coolers-ice-chests", "hardwood-floor", "trash-cans-outdoor"],
  },
  truck: {
    zones: ["Service window", "Line", "Sinks", "Tanks", "Cab"],
    area: { cooking_line: "Line", prep: "Line", dish: "Sinks", cold_storage: "Line", dry_storage: "Line", bar_beverage: "Service window" },
    put: { "hand-sink": "Sinks", "prep-sink": "Sinks", "grease-trap": "Tanks", "payment-screen": "Service window", "condiment-station": "Service window", "door-handles": "Cab", "windows-glass": "Service window", "kitchen-floor": "Line", "trash-cans-indoor": "Line" },
    skip: ["walk-in", "slicer", "hood-filters", "grill-grates", "coolers-ice-chests", "ice-machine"],
  },
  church_hall: {
    zones: ["Kitchen", "Serving line", "Hall", "Restroom", "Storage"],
    area: { cooking_line: "Kitchen", prep: "Kitchen", dish: "Kitchen", bar_beverage: "Serving line", cold_storage: "Storage", dry_storage: "Storage", front_of_house: "Hall", restroom: "Restroom", floors_walls: "Kitchen" },
    put: { "drip-brewer": "Serving line", tongs: "Serving line", "condiment-station": "Serving line", "dining-floor": "Hall", "trash-cans-indoor": "Kitchen", "trash-cans-outdoor": "Hall" },
    skip: ["slicer", "fryer", "walk-in", "payment-screen", "pastry-display-case", "hardwood-floor", "grease-trap"],
  },
};

/** The zone preset for a place: café for coffee shops, truck for mobile permits, church hall for community spaces. */
export function zonePresetFor(p: { orgKind?: OrgKind; permitType?: PermitType }): ZonePreset {
  if (p.permitType === "mobile_food_business") return "truck";
  if (p.orgKind === "coffee_shop") return "cafe";
  if (p.orgKind === "community_space" || p.permitType === "private_members_only" || p.permitType === "temporary_event") return "church_hall";
  return "restaurant";
}

const PERMITS: PermitType[] = ["food_establishment", "temporary_event", "mehk", "private_members_only", "mobile_food_business"];

/** A business type id (opening.ts BUSINESS_TYPES) or a permit type → its zone preset. Unknown → restaurant. */
export function zonePresetOf(type: string): ZonePreset {
  if ((Object.keys(PRESETS) as string[]).includes(type)) return type as ZonePreset;
  const b = businessType(type);
  if (b) return zonePresetFor({ orgKind: b.orgKind, permitType: b.permitType });
  if ((PERMITS as string[]).includes(type)) return zonePresetFor({ permitType: type as PermitType });
  return "restaurant";
}

/** Zones per preset. */
export const ZONE_PRESETS: Record<ZonePreset, string[]> = Object.fromEntries(Object.entries(PRESETS).map(([k, v]) => [k, v.zones])) as Record<ZonePreset, string[]>;

/** Zones for every business type id (opening.ts BUSINESS_TYPES), plus the four presets by name. */
export const ZONES_BY_TYPE: Record<string, string[]> = {
  ...ZONE_PRESETS,
  ...Object.fromEntries(BUSINESS_TYPES.filter((b) => b.supported).map((b) => [b.id, PRESETS[zonePresetOf(b.id)].zones])),
};

/**
 * The rooms a new location starts with, used by Report's place picker: its type's zones (café: Bar, Front counter,
 * Seating, Restroom, Storage, Outside). A business type id wins; otherwise org kind + permit type pick the preset.
 */
export function defaultAreasFor(p: { businessTypeId?: string; orgKind?: OrgKind; permitType?: PermitType }): string[] {
  const byType = p.businessTypeId ? ZONES_BY_TYPE[p.businessTypeId] : undefined;
  return [...(byType ?? ZONE_PRESETS[zonePresetFor(p)])];
}

/** "Add rooms like yours": the location's areas plus the zones it doesn't have yet (case-insensitive), in order. */
export function mergeAreas(areas: readonly string[], zones: readonly string[]): string[] {
  const out = [...areas];
  for (const z of zones) if (!out.some((a) => a.toLowerCase() === z.toLowerCase())) out.push(z);
  return out;
}

export type StarterObject = Pick<PlaceObject, "name" | "surfaceId" | "zone" | "count">;

/** Starter objects for a business type id or permit type: one per surface guide in the preset's areas, in zone order. */
export function suggestObjects(type: string): StarterObject[] {
  const p = PRESETS[zonePresetOf(type)];
  const out: StarterObject[] = [];
  for (const s of SURFACES) {
    if (p.skip?.includes(s.id) || s.reference) continue;
    const zone = p.put?.[s.id] ?? p.area[s.area];
    if (zone) out.push({ name: s.name, surfaceId: s.id, zone });
  }
  return out.sort((a, b) => p.zones.indexOf(a.zone) - p.zones.indexOf(b.zone));
}

/* ─────────────── intervals ─────────────── */

const UNIT_HOURS: Record<string, number> = { hour: 1, hr: 1, day: 24, week: 168 };

/**
 * Hours from a howOften line that states a number: "every 4 hours" → 4, "at least every 24 hours" → 24,
 * "every 2 days" → 48. Anything without a stated number ("daily", "every few hours", "keep clean"), a range
 * ("every 3–6 months") or a unit longer than a week (professional service) → undefined.
 */
export function parseIntervalHours(text: string): number | undefined {
  const m = /\bevery\s+(\d+(?:\.\d+)?)\s*(hours?|hrs?|days?|weeks?)\b/i.exec(text);
  if (!m) return undefined;
  const n = Number(m[1]);
  const unit = m[2]!.toLowerCase().replace(/s$/, "");
  const h = n * (UNIT_HOURS[unit] ?? NaN);
  return Number.isFinite(h) && h > 0 ? h : undefined;
}

export const SUGGESTED_INTERVAL_HOURS = 24;
export const SUGGESTED_REASON = "No official interval; a daily clean is a common starting point";

/** The object's interval: the shortest numeric howOften on its surface guide, else the suggested daily clean. */
export function intervalFor(surface: SurfaceGuide | undefined): { hours: number; suggested: boolean; reason: string } {
  let best: { hours: number; reason: string } | undefined;
  for (const h of surface?.howOften ?? []) {
    const n = parseIntervalHours(h.when);
    if (n !== undefined && (!best || n < best.hours)) best = { hours: n, reason: `${h.when} (${h.citation})` };
  }
  return best ? { ...best, suggested: false } : { hours: SUGGESTED_INTERVAL_HOURS, suggested: true, reason: SUGGESTED_REASON };
}

/** Clean or maintain: descaling, filters and the grease trap are upkeep; everything else is cleaning. */
export function objectKind(obj: Pick<PlaceObject, "surfaceId">): "clean" | "maintain" {
  const s = getSurface(obj.surfaceId);
  return s && (/descal|grease trap/i.test(s.name) || s.id === "hood-filters") ? "maintain" : "clean";
}

/** The products an object's guide uses, deduped by supply key (GetIt: Kept · Reorder/Call · Find it on Amazon). */
export function objectNeeds(obj: Pick<PlaceObject, "surfaceId">): ActivityNeed[] {
  const s = getSurface(obj.surfaceId);
  const out: ActivityNeed[] = [];
  const seen = new Set<string>();
  for (const p of s?.products ?? []) {
    const supplyKey = supplyKeyFor(p);
    const k = supplyKey ?? p.purpose;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ ...(supplyKey ? { supplyKey } : {}), product: p.examples[0] ?? p.purpose });
  }
  return out;
}

/* ─────────────── state ─────────────── */

export interface ObjectStateInput {
  events: (Pick<ActivityEvent, "objectId" | "kind" | "at"> & { by?: string })[];
  openReports: Pick<Incident, "id" | "area" | "title" | "details" | "status">[];
  supplies: Supply[];
  now: Date | IsoDateTime;
  /**
   * Intervals learned from the team's own Done history, by object id (quicklog.ts learnedIntervals). A learned interval
   * replaces the suggested daily one; against a sourced interval, the stricter (shorter) one applies, so a learned
   * interval never loosens a sourced rule.
   */
  learnedHours?: Record<Id, number>;
}

/** The interval to judge an object by: sourced or suggested (intervalFor), with a learned one applied as described above. */
export function effectiveInterval(surface: SurfaceGuide | undefined, learnedHours?: number): { hours: number; suggested: boolean; reason: string; learned?: true } {
  const iv = intervalFor(surface);
  if (!learnedHours || !(learnedHours > 0)) return iv;
  const every = `about every ${learnedHours < 1 ? `${Math.round(learnedHours * 60)} minutes` : `${learnedHours} ${learnedHours === 1 ? "hour" : "hours"}`}`;
  if (iv.suggested) return { hours: learnedHours, suggested: false, learned: true, reason: `Learned from your team's logs: ${every}` };
  if (learnedHours < iv.hours) return { hours: learnedHours, suggested: false, learned: true, reason: `Learned from your team's logs: ${every}. The rule: ${iv.reason}` };
  return iv;
}

const lower = (s: string | undefined) => (s ?? "").trim().toLowerCase();

/** An open report is on this object when its area is the object's zone, or its words name the object. */
export function reportOn(obj: Pick<PlaceObject, "name" | "zone">, r: Pick<Incident, "area" | "title" | "details" | "status">): boolean {
  if (r.status === "resolved") return false;
  if (lower(r.area) === lower(obj.zone)) return true;
  const n = lower(obj.name);
  return n.length >= 3 && (lower(r.title).includes(n) || lower(r.details).includes(n));
}

/**
 * State of one object. Precedence: problem > out_of_stock > overdue > due > clean > not_started (O3).
 * Timing: clean until lastAt + interval; due from then for half an interval; overdue after that.
 */
export function objectState(obj: Pick<PlaceObject, "id" | "name" | "zone" | "surfaceId">, input: ObjectStateInput): ObjectState {
  const surface = getSurface(obj.surfaceId);
  const iv = effectiveInterval(surface, input.learnedHours?.[obj.id]);
  const base = { intervalHours: iv.hours, suggested: iv.suggested, ...(iv.learned ? { learned: true as const } : {}) };
  const kind = objectKind(obj);
  const lastEv = input.events.filter((e) => counts(e) && e.objectId === obj.id && e.kind === kind).reduce<(Pick<ActivityEvent, "objectId" | "kind" | "at"> & { by?: string }) | undefined>((m, e) => (!m || e.at > m.at ? e : m), undefined);
  const last = lastEv?.at;
  const nowMs = typeof input.now === "string" ? Date.parse(input.now) : input.now.getTime();
  const dueAt = last ? new Date(Date.parse(last) + iv.hours * 3_600_000).toISOString() : undefined;
  const timing = { ...base, ...(last ? { lastAt: last, ...(lastEv!.by ? { lastBy: lastEv!.by } : {}), dueAt } : {}) };

  const report = input.openReports.find((r) => reportOn(obj, r));
  if (report) return { ...timing, state: "problem", reason: `Problem reported: ${report.title}` };
  const keys = new Set(objectNeeds(obj).map((n) => n.supplyKey).filter(Boolean));
  const out = input.supplies.find((s) => keys.has(s.key) && s.onHand !== undefined && s.onHand <= 0);
  if (out) return { ...timing, state: "out_of_stock", reason: `Out of ${out.name}` };
  if (!last) return { ...base, state: "not_started", reason: `Not marked done yet. ${iv.reason}` };
  const dueMs = Date.parse(dueAt!);
  const state: ObjectStateName = nowMs < dueMs ? "clean" : nowMs < dueMs + (iv.hours * 3_600_000) / 2 ? "due" : "overdue";
  return { ...timing, state, reason: iv.reason };
}

/* ─────────────── routine ─────────────── */

export interface RoutineInput {
  /** Today's expected checks (schedule.ts); `satisfied: true` ones are already done and left out. */
  expected: (ExpectedCheck & { satisfied?: boolean })[];
  tasks: (Pick<TaskDefinition, "id" | "title" | "category"> & Partial<Pick<TaskDefinition, "how">>)[];
  objects: PlaceObject[];
  events: ActivityEvent[];
  openReports: Pick<Incident, "id" | "area" | "title" | "details" | "status" | "reportedAt">[];
  supplies: Supply[];
  now: Date | IsoDateTime;
  /** Learned intervals by object id (ObjectStateInput.learnedHours). */
  learnedHours?: Record<Id, number>;
}

/** A task's How: the sourced guides and library topic its starter seed links (tasks.ts), the seed key, and its own one-line how. */
function taskHow(task: { title: string; how?: string }): ActivityItem["how"] | undefined {
  const seed = seedForTask(task);
  const text = task.how?.trim();
  const also = (seed?.alsoSurfaceIds ?? []).filter((id) => getSurface(id));
  const how = {
    ...(seed?.surfaceId && getSurface(seed.surfaceId) ? { surfaceId: seed.surfaceId } : {}), ...(seed ? { taskKey: seed.key } : {}), ...(text ? { text } : {}),
    ...(also.length ? { alsoSurfaceIds: also } : {}), ...(seed?.libraryId ? { libraryId: seed.libraryId } : {}),
  };
  return Object.keys(how).length ? how : undefined;
}

/** Today's routine: one mixed list in routine() order (report, log, clean, maintain, stock), each with How and needs. */
export function routineFor(input: RoutineInput): ActivityItem[] {
  const items: ActivityItem[] = [];
  const taskById = new Map(input.tasks.map((t) => [t.id, t]));
  for (const e of input.expected) {
    if (e.satisfied) continue;
    const task = e.taskId ? taskById.get(e.taskId) : undefined;
    if (e.taskId) {
      const how = task ? taskHow(task) : undefined;
      items.push({ kind: task ? kindOfTask(task) : "log", id: `task:${e.taskId}:${e.key}`, ref: { type: "task", id: e.taskId }, title: e.label, dueAt: e.dueAt, ...(e.firstDay ? { firstDay: true as const } : {}), ...(how ? { how } : {}), needs: [] });
      continue;
    }
    items.push({
      kind: "log", id: `check:${e.key}`, ref: { type: "check", id: e.key }, title: e.label, dueAt: e.dueAt, ...(e.firstDay ? { firstDay: true as const } : {}), how: { templateId: e.templateId },
      needs: (e.howTo?.needSupplies ?? []).map((n) => ({ supplyKey: n.key, product: n.product })),
    });
  }
  for (const o of input.objects) {
    // Timing only (reports and stock have their own rows): a problem on the zone doesn't hide that it's due.
    const st = objectState(o, { events: input.events, openReports: [], supplies: [], now: input.now, ...(input.learnedHours ? { learnedHours: input.learnedHours } : {}) });
    if (st.state !== "due" && st.state !== "overdue" && st.state !== "not_started") continue;
    items.push({ kind: objectKind(o), id: `object:${o.id}`, ref: { type: "object", id: o.id }, title: o.count && o.count > 1 ? `${o.name} ×${o.count}` : o.name, ...(st.dueAt ? { dueAt: st.dueAt } : {}), how: { surfaceId: o.surfaceId }, needs: objectNeeds(o) });
  }
  for (const r of input.openReports) {
    if (r.status === "resolved") continue;
    items.push({ kind: "report", id: `incident:${r.id}`, ref: { type: "incident", id: r.id }, title: r.title, needs: [] });
  }
  for (const s of input.supplies) {
    // Below par only: at par you have what you need (owner scrutiny of the demo's noisy Stock rows, 2026-09-28).
    if (s.par === undefined || s.onHand === undefined || s.onHand >= s.par) continue;
    items.push({ kind: "stock", id: `supply:${s.key}`, ref: { type: "supply", id: s.key }, title: `Restock ${s.name}`, needs: [{ supplyKey: s.key, product: s.name }] });
  }
  return routine(items);
}
